import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CustomMenuItemService } from './custom-menu-item.service';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';

// ✅ API Response interfaces
interface ApiMenuData {
  system_function_id: number;
  display_name: string;
  system_group: string;
  system_sub_group: string;
  menu_short_cut_id: number;
  is_in_manager_page: string;
  is_in_user_page: string;
  is_in_userdefined_page1: string;
  is_in_userdefined_page2: string;
  is_in_userdefined_page3: string;
}

interface ApiResponse {
  status: string;
  message: string;
  data: {
    custom_menus: ApiMenuData[];
  };
  serverTime: string;
  token: string;
}

// ✅ Component interfaces (keeping existing structure)
interface MenuFunction {
  id: string;
  function: string;
  manager: boolean;
  user: boolean;
  sushi_manager_extra: boolean;
  assistant_manager: boolean;
  trainee_manager: boolean;
}

interface MenuCategory {
  title: string;
  items: MenuFunction[];
}

interface Role {
  key: string;
  label: string;
}

@Component({
  selector: 'app-custom-menu-item',
  standalone: false,
  templateUrl: './custom-menu-item.component.html',
  styleUrl: './custom-menu-item.component.scss'
})
export class CustomMenuItemComponent implements OnInit {

  canEdit = true;
  isSaving = false;
  
  // ✅ Keep existing roles structure
  roles: Role[] = [
    { key: 'manager', label: 'Manager' },
    { key: 'user', label: 'User' },
    { key: 'sushi_manager_extra', label: 'Sushi Manager Extra' },
    { key: 'assistant_manager', label: 'Assistant Manager' },
    { key: 'trainee_manager', label: 'Trainee Manager' }
  ];

  // ✅ Initialize empty, will be populated from API
  menuCategories: MenuCategory[] = [];
  rawApiData: ApiMenuData[] = [];

  constructor(private http: HttpClient, private customMenuItemService: CustomMenuItemService,    private snackBarService: SnackBarService,
) {}

  ngOnInit(): void {
    this.loadMenuShortcuts();
  }

  // ✅ Load data from API and organize it
  loadMenuShortcuts(): void {
    
    this.customMenuItemService.getList().subscribe({
      next: (response: ApiResponse) => {
        console.log('📡 API Response:', response);
        
        if (response.status === 'SUCCESS' && response.data?.custom_menus?.length > 0) {
          this.rawApiData = response.data.custom_menus;
          this.organizeMenuData();
          console.log('✅ Menu shortcuts loaded and organized:', this.menuCategories);
        } else {
          console.error('❌ No menu shortcuts data found');
        }
      },
      error: (error) => {
        console.error('💥 API Error:', error);
      }
    });
  }

  // ✅ Convert API data to component format
  private organizeMenuData(): void {
    const groupedData: { [key: string]: MenuFunction[] } = {};

    // Group by system_group and system_sub_group
    this.rawApiData.forEach(apiItem => {
      const categoryKey = `${apiItem.system_group}-${apiItem.system_sub_group}`;
      
      if (!groupedData[categoryKey]) {
        groupedData[categoryKey] = [];
      }
      
      // Convert API data to MenuFunction format using system_function_id as id
      const menuFunction: MenuFunction = {
        id: apiItem.system_function_id.toString(),
        function: apiItem.display_name.trim(), // Remove extra spaces
        manager: apiItem.is_in_manager_page === '1',
        user: apiItem.is_in_user_page === '1',
        sushi_manager_extra: apiItem.is_in_userdefined_page1 === '1',
        assistant_manager: apiItem.is_in_userdefined_page2 === '1',
        trainee_manager: apiItem.is_in_userdefined_page3 === '1'
      };
      
      groupedData[categoryKey].push(menuFunction);
    });

    // Convert to MenuCategory array with proper title formatting
    this.menuCategories = Object.keys(groupedData).map(key => {
      const [group, subGroup] = key.split('-');
      const title = subGroup && subGroup !== group ? `${group} > ${subGroup}` : group;
      
      return {
        title: title,
        items: groupedData[key].sort((a, b) => parseInt(a.id) - parseInt(b.id))
      };
    });

    // Sort categories alphabetically
    this.menuCategories.sort((a, b) => a.title.localeCompare(b.title));
  }

  // ✅ Check if individual item is checked
  isChecked(itemId: string, roleKey: string): boolean {
    const category = this.menuCategories.find(cat => 
      cat.items.some(item => item.id === itemId)
    );
    const item = category?.items.find(item => item.id === itemId);
    return item ? item[roleKey as keyof MenuFunction] as boolean : false;
  }

  // ✅ Handle individual checkbox changes
  onPermissionChange(itemId: string, roleKey: string, checked: boolean): void {
    const category = this.menuCategories.find(cat => 
      cat.items.some(item => item.id === itemId)
    );
    const item = category?.items.find(item => item.id === itemId);
    
    if (item) {
      (item as any)[roleKey] = checked;
      console.log(`Permission changed - Item: ${itemId}, Role: ${roleKey}, Checked: ${checked}`);
    }
  }

  // ✅ Check if all items are selected for a role (header checkbox)
  isAllSelected(roleKey: string): boolean {
    const allItems = this.getAllItems();
    return allItems.length > 0 && allItems.every(item => 
      item[roleKey as keyof MenuFunction] as boolean
    );
  }

  // ✅ Check if some items are selected for a role (indeterminate state)
  isIndeterminate(roleKey: string): boolean {
    const allItems = this.getAllItems();
    const checkedItems = allItems.filter(item => 
      item[roleKey as keyof MenuFunction] as boolean
    );
    return checkedItems.length > 0 && checkedItems.length < allItems.length;
  }

  // ✅ Toggle all items for a specific role (header checkbox click)
  toggleAllForRole(roleKey: string, checked: boolean): void {
    this.menuCategories.forEach(category => {
      category.items.forEach(item => {
        (item as any)[roleKey] = checked;
      });
    });
    
    console.log(`All items ${checked ? 'selected' : 'deselected'} for role: ${roleKey}`);
  }

  // ✅ Get all items from all categories
  private getAllItems(): MenuFunction[] {
    return this.menuCategories.reduce((acc, category) => {
      return acc.concat(category.items);
    }, [] as MenuFunction[]);
  }

  // ✅ Track by function for performance
  trackByKey(index: number, item: any): string {
    return item.id?.toString() || item.title || index.toString();
  }

  // ✅ Get display number for items (sequential numbering)
  getDisplayNumber(categoryIndex: number, itemIndex: number): number {
    let count = 1;
    
    // Count items in previous categories
    for (let i = 0; i < categoryIndex; i++) {
      count += this.menuCategories[i].items.length;
    }
    
    // Add current item index
    count += itemIndex;
    
    return count;
  }

  // ✅ Reset to default
  resetToDefault(): void {
    if (confirm('Are you sure you want to discard all changes?')) {
      this.loadMenuShortcuts();
    }
  }

  // ✅ Save changes (convert back to API format)
  saveChanges(): void {
    this.isSaving = true;
    
    // Prepare data for API save with exact format as requested
    const saveData = {
      custom_menus: this.rawApiData.map(apiItem => {
        // Find the corresponding UI item to get current permission states
        const uiItem = this.getAllItems().find(item => item.id === apiItem.system_function_id.toString());
        
        return {
          system_function_id: apiItem.system_function_id,
          display_name: apiItem.display_name,
          system_group: apiItem.system_group,
          system_sub_group: apiItem.system_sub_group,
          menu_short_cut_id: apiItem.menu_short_cut_id,
          is_in_manager_page: uiItem ? (uiItem.manager ? '1' : '0') : apiItem.is_in_manager_page,
          is_in_user_page: uiItem ? (uiItem.user ? '1' : '0') : apiItem.is_in_user_page,
          is_in_userdefined_page1: uiItem ? (uiItem.sushi_manager_extra ? '1' : '0') : apiItem.is_in_userdefined_page1,
          is_in_userdefined_page2: uiItem ? (uiItem.assistant_manager ? '1' : '0') : apiItem.is_in_userdefined_page2,
          is_in_userdefined_page3: uiItem ? (uiItem.trainee_manager ? '1' : '0') : apiItem.is_in_userdefined_page3
        };
      })
    };

    console.log('💾 Saving menu shortcuts:', saveData);
    
    // Call update API
    this.customMenuItemService.update(saveData).subscribe({
      next: (response) => {
        this.isSaving = false;
        console.log('✅ Menu shortcuts saved successfully:', response);
        
        let snackBarData: SnackBarData = {
          message: response.message || 'Menu shortcuts updated successfully!',
          title: 'Success!',
          type: 'success'
        };
        this.snackBarService.openSnackBar(snackBarData);
      },
      error: (error) => {
        this.isSaving = false;
        console.error('💥 Save Error:', error);
        
        let snackBarData: SnackBarData = {
          message: error.error?.message || 'Failed to save menu shortcuts. Please try again.',
          title: 'Failed!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }
}